<template lang="pug">
.family-message-history-detail-family-list-accordioin
  // unread accordion
  .unread-accordion(v-if="type == 0")
    b-card-header#read-unread.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle.accordion-read-unread)
      span.toggle-text 未読施設関係者（{{ otherUsers.length }}件）
    b-collapse#accordion-read-unread(accordion="read-unread-accordion" role="tabpanel")
      b-card-body.p-2
        AccordionLoading(:isBusy="isBusy")
        MessageHistoryOtherUserList(v-show="!isBusy" v-model="otherUsers" :otherUserGroupTypeMap="otherUserGroupTypeMap")
  // readed accordion
  .readed-accordion(v-if="type == 1")
    b-card-header#read-unread.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle.accordion-read-readed)
      span.toggle-text 既読施設関係者（{{ otherUsers.length }}件）
    b-collapse#accordion-read-readed(accordion="read-readed-accordion" role="tabpanel")
      b-card-body.p-2
        AccordionLoading(:isBusy="isBusy")
        MessageHistoryOtherUserList(v-show="!isBusy" v-model="otherUsers" :otherUserGroupTypeMap="otherUserGroupTypeMap")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileMessageHistoryState } from "@/store/MobileMessageHistory";

import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";

import MessageHistoryOtherUserList from "@/mobile/components/lists/MessageHistoryOtherUserList.vue";
import AccordionLoading from "@/mobile/basics/AccordionLoading.vue";

/**
 * ### 送信履歴詳細(施設関係者)送信メッセージ未読/既読の施設関係者リスト
 *
 * - 表示対象に応じて未読施設関係者リストか既読施設関係者リストを表示するか判断する
 */
@Component({
  components: {
    AccordionLoading,
    MessageHistoryOtherUserList
  }
})
export default class OtherUserListAccordioin extends Vue {
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;

  /** ### 新規送信入力画面での入力情報 */
  @Getter("mobileMessageHistoryState") state!: MobileMessageHistoryState;

  /** 表示対象(既読施設関係者、未読施設関係者) */
  @Prop({ required: true })
  type!: OtherUserListAccordioinType;

  /** ローディングの表示可否 */
  isBusy: boolean = false;
  /** 施設関係者グループ種類MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map<number, string>();
  /** 表示対象施設関係者 */
  otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = [];

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 画面表示用に返信未読数をカウントする
   * - 未読・既読の施設関係者を表示するコンポーネント用の情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 後で使用するため、施設関係者グループ種類をMAP化しておく
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    // 未読・既読の施設関係者を表示するコンポーネント用の情報を作成する
    const readOtherUser: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadOtherUsers();
    let unreadOtherUser: Array<OtherUserGroupSendMessageReadMemberModel> = [];
    let readedOtherUser: Array<OtherUserGroupSendMessageReadMemberModel> = [];
    for (let otherUser of readOtherUser) {
      if (otherUser.readDatetime != undefined || otherUser.readDatetime != null) {
        readedOtherUser.push(otherUser);
      } else {
        unreadOtherUser.push(otherUser);
      }
    }

    if (this.type == OtherUserListAccordioinType.UNREAD_OTHERUSER) {
      this.otherUsers = unreadOtherUser;
    } else if (this.type == OtherUserListAccordioinType.READED_OTHERUSER) {
      this.otherUsers = readedOtherUser;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者を取得する
   */
  public async getSendMessageReadOtherUsers(): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, Number(this.state.datetime));

    return members;
  }
}

export enum OtherUserListAccordioinType {
  UNREAD_OTHERUSER = 0,
  READED_OTHERUSER = 1
}
</script>
